import axios from 'axios';
import { initializeApp, getApps } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';
import { config } from '../config';
import {
    firebaseWebConfig,
    firebaseVapidKey,
    isFirebaseEnabled,
} from '../firebase/config';
import formatNotification from './notification';

let messagingInstance = null;
let registeredToken = null;
let foregroundListenerAttached = false;

const log = config.socket?.log ?? false;

async function registerServiceWorker() {
    if (!('serviceWorker' in navigator)) {
        return null;
    }

    try {
        return await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    } catch (error) {
        log && console.warn('Firebase SW registration failed:', error.message);
        return null;
    }
}

function mapFcmPayload(payload) {
    const data = payload.data || {};

    return formatNotification({
        kind: data.Kind || data.kind,
        temp_id: data.TempId || data.temp_id,
        avatar: data.Avatar || data.avatar,
        message:
            data.Message ||
            data.message ||
            payload.notification?.title,
        detail:
            data.Detail ||
            data.detail ||
            payload.notification?.body,
        created_at: data.CreatedAt || data.created_at,
        to_user_id: data.ToUserId || data.to_user_id,
        user_id: data.UserId || data.user_id,
    });
}

async function initMessaging() {
    if (!isFirebaseEnabled()) {
        return null;
    }

    const supported = await isSupported();
    if (!supported) {
        log && console.warn('Firebase messaging is not supported in this browser');
        return null;
    }

    try {
        const app = getApps().length
            ? getApps()[0]
            : initializeApp(firebaseWebConfig);
        messagingInstance = getMessaging(app);
        return messagingInstance;
    } catch (error) {
        log && console.warn('Firebase messaging init failed:', error.message);
        return null;
    }
}

export async function registerDeviceToken(userId, accessToken) {
    if (!userId || !accessToken || !isFirebaseEnabled()) {
        return;
    }

    const messaging = await initMessaging();
    if (!messaging) {
        return;
    }

    try {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
            log && console.warn('Notification permission not granted');
            return;
        }

        const serviceWorkerRegistration = await registerServiceWorker();
        const token = await getToken(messaging, {
            vapidKey: firebaseVapidKey,
            serviceWorkerRegistration: serviceWorkerRegistration || undefined,
        });

        if (!token || token === registeredToken) {
            return;
        }

        await axios.post(
            config.api.godiway + 'auth/moken',
            {
                id: userId,
                fcm_token: token,
                os: 'web',
            },
            {
                headers: { Authorization: `Bearer ${accessToken}` },
            }
        );

        registeredToken = token;
        log && console.log('FCM token registered with backend');
    } catch (error) {
        log && console.warn('FCM token registration failed:', error.message);
    }
}

export async function unregisterDeviceToken(userId, accessToken) {
    if (!userId || !accessToken) {
        return;
    }

    let token = registeredToken;

    if (!token && isFirebaseEnabled()) {
        const messaging = await initMessaging();
        if (messaging) {
            try {
                const serviceWorkerRegistration = await registerServiceWorker();
                token = await getToken(messaging, {
                    vapidKey: firebaseVapidKey,
                    serviceWorkerRegistration: serviceWorkerRegistration || undefined,
                });
            } catch (error) {
                log && console.warn('FCM getToken on logout failed:', error.message);
            }
        }
    }

    if (!token) {
        return;
    }

    try {
        await axios.delete(config.api.godiway + 'auth/moken', {
            data: {
                id: userId,
                fcm_token: token,
            },
            headers: { Authorization: `Bearer ${accessToken}` },
        });
        log && console.log('FCM token unregistered from backend');
    } catch (error) {
        log && console.warn('FCM token unregister failed:', error.message);
    } finally {
        registeredToken = null;
        foregroundListenerAttached = false;
    }
}

export function setupForegroundMessaging(
    dispatch,
    receiveNotification,
    receiveEventNotification
) {
    if (!messagingInstance || foregroundListenerAttached) {
        return;
    }

    onMessage(messagingInstance, (payload) => {
        const notification = mapFcmPayload(payload);

        if (notification.Kind === 'MESSAGE') {
            return;
        }

        dispatch(receiveNotification(notification));
        dispatch(receiveEventNotification());
    });

    foregroundListenerAttached = true;
}

export async function setupFirebaseMessaging(
    userId,
    accessToken,
    dispatch,
    receiveNotification,
    receiveEventNotification
) {
    await registerDeviceToken(userId, accessToken);
    setupForegroundMessaging(
        dispatch,
        receiveNotification,
        receiveEventNotification
    );
}
